/**
 * Legend — dynamic map legend that slides in when data is displayed.
 * Supports collapsible tier legend for choropleth visualizations
 * with 7-level log-quantile classification and scale mode indicator.
 */
import DOMPurify from 'dompurify'
import { formatNumber } from '../utils/geo-utils.js'

class Legend {
  constructor() {
    this.panel = document.createElement('div')
    this.panel.className = 'legend-panel'
    this.panel.innerHTML = ''
    this._collapsed = true
    this._currentConfig = null
    document.getElementById('ui-overlay').appendChild(this.panel)
  }

  /**
   * Update the legend with new data.
   * @param {object} config - Legend configuration
   * @param {boolean} config.visible - Show or hide
   * @param {string} config.title - Legend title
   * @param {string} config.type - 'gradient', 'classified', or 'categorical'
   * @param {Array} config.stops - Color stops
   * @param {string} [config.scaleMode] - e.g. "Global scale" or "Local scale (ADM0)"
   * @param {string} [config.unit] - Metric unit
   * @param {boolean} [config.collapsible] - If true, render as collapsible legend
   */
  update(config) {
    if (!config.visible) {
      this.panel.classList.remove('visible')
      this.panel.innerHTML = ''
      this._currentConfig = null
      return
    }

    this._currentConfig = config

    if (config.type === 'bivariate') {
      this._renderBivariate(config)
    } else if (config.collapsible) {
      this._renderCollapsible(config)
    } else {
      this._collapsed = true
      this._renderStandard(config)
    }

    this.panel.classList.add('visible')
  }

  /**
   * Render the standard (non-collapsible) legend.
   */
  _renderStandard(config) {
    let html = `<div class="legend-title">${config.title || 'Legend'}</div>`

    // ── Pagination header (scheme switcher arrows) ──────────
    if (config.pagination) {
      const pg = config.pagination
      html += `<div class="legend-pagination">`
      html += `<button class="legend-page-btn" data-dir="prev" aria-label="Previous scheme">`
      html += `<svg viewBox="0 0 16 16" width="14" height="14"><path d="M10 4l-4 4 4 4" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>`
      html += `</button>`
      html += `<span class="legend-page-label">${pg.label}</span>`
      html += `<button class="legend-page-btn" data-dir="next" aria-label="Next scheme">`
      html += `<svg viewBox="0 0 16 16" width="14" height="14"><path d="M6 4l4 4-4 4" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>`
      html += `</button>`
      html += `</div>`
    }

    if (config.type === 'classified' && config.stops) {
      html += `<div class="legend-items">`
      for (const stop of config.stops) {
        const label = stop.label || `${formatNumber(stop.min)} – ${formatNumber(stop.max)}`
        const colorStr = this._colorStr(stop.color)
        html += `<div class="legend-item">`
        html += `<div class="legend-swatch" style="background: ${colorStr}"></div>`
        html += `<span>${label}</span>`
        html += `</div>`
      }
      html += `</div>`
    } else if (config.type === 'gradient' && config.stops) {
      const colors = config.stops.map((s) => this._colorStr(s.color)).join(', ')
      html += `<div class="legend-gradient" style="background: linear-gradient(to right, ${colors})"></div>`
      html += `<div class="legend-labels">`
      html += `<span>${config.stops[0]?.label ?? formatNumber(config.stops[0]?.value)}</span>`
      html += `<span>${config.stops[config.stops.length - 1]?.label ?? formatNumber(config.stops[config.stops.length - 1]?.value)}</span>`
      html += `</div>`
    } else if (config.type === 'categorical' && config.stops) {
      const cap = config.maxItems ?? 10
      html += `<div class="legend-items">`
      for (const stop of config.stops.slice(0, cap)) {
        const colorStr = this._colorStr(stop.color)
        const label = stop.label || stop.value || ''
        html += `<div class="legend-item">`
        html += `<div class="legend-swatch" style="background: ${colorStr}"></div>`
        html += `<span>${label}</span>`
        html += `</div>`
      }
      if (config.stops.length > cap) {
        html += `<div class="legend-item"><span style="color: var(--text-muted)">+${config.stops.length - cap} more</span></div>`
      }
      html += `</div>`
    }

    // SECURITY: sanitized 2026-04-18 — config.title and stop.label may derive from R2/AI
    this.panel.innerHTML = DOMPurify.sanitize(html)

    // ── Bind pagination arrow click handlers ─────────────────
    if (config.pagination) {
      const pg = config.pagination
      this.panel.querySelectorAll('.legend-page-btn').forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.stopPropagation()
          const dir = btn.dataset.dir
          const next =
            dir === 'next' ? (pg.current + 1) % pg.total : (pg.current - 1 + pg.total) % pg.total
          pg.onSwitch(next)
        })
      })
    }
  }

  /**
   * Render the collapsible choropleth legend.
   * Collapsed: compact pill with mini color bar.
   * Expanded: full bracket list with scale mode indicator.
   */
  _renderCollapsible(config) {
    const stops = config.stops || []
    const dataStops = stops.filter((s) => s.bracket !== 0) // exclude "No data"

    if (this._collapsed) {
      let html = `<div class="tier-legend-collapsed" data-action="expand">`
      html += `<div class="tier-legend-pill">`
      html += `<div class="tier-mini-bar">`
      for (const stop of dataStops) {
        const colorStr = this._colorStr(stop.color)
        html += `<div class="tier-mini-segment" style="background:${colorStr}"></div>`
      }
      html += `</div>`
      html += `<span class="tier-legend-label">${config.title || 'Legend'}</span>`
      html += `<svg class="tier-expand-icon" viewBox="0 0 16 16" width="12" height="12">`
      html += `<path d="M4 6l4 4 4-4" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>`
      html += `</svg>`
      html += `</div>`
      html += `</div>`
      // SECURITY: sanitized 2026-04-18 — config.title may derive from R2/AI
      this.panel.innerHTML = DOMPurify.sanitize(html)

      this.panel.querySelector('[data-action="expand"]')?.addEventListener('click', () => {
        this._collapsed = false
        this._renderCollapsible(config)
      })
    } else {
      let html = `<div class="tier-legend-expanded">`
      // Header with collapse button
      html += `<div class="tier-legend-header" data-action="collapse">`
      html += `<div>`
      html += `<span class="legend-title">${config.title || 'Legend'}</span>`
      if (config.unit) {
        html += `<span class="legend-unit">${config.unit}</span>`
      }
      html += `</div>`
      html += `<svg class="tier-collapse-icon" viewBox="0 0 16 16" width="12" height="12">`
      html += `<path d="M4 10l4-4 4 4" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>`
      html += `</svg>`
      html += `</div>`

      // Scale mode indicator
      if (config.scaleMode) {
        html += `<div class="legend-scale-mode">${config.scaleMode}</div>`
      }

      // Bracket entries
      html += `<div class="tier-legend-items">`
      for (const stop of stops) {
        const colorStr = this._colorStr(stop.color)
        html += `<div class="legend-item">`
        html += `<div class="legend-swatch" style="background: ${colorStr}"></div>`
        html += `<span>${stop.label}</span>`
        html += `</div>`
      }
      html += `</div>`
      html += `</div>`
      // SECURITY: sanitized 2026-04-18 — config.title, config.unit, stop.label may derive from R2/AI
      this.panel.innerHTML = DOMPurify.sanitize(html)

      this.panel.querySelector('[data-action="collapse"]')?.addEventListener('click', () => {
        this._collapsed = true
        this._renderCollapsible(config)
      })
    }
  }

  /**
   * Render a 3×3 bivariate choropleth legend.
   * Shows a color grid with axis labels for both variables.
   */
  _renderBivariate(config) {
    const { palette, attribute1, attribute2, noDataColor } = config
    const size = 28 // cell size in px
    const gap = 2

    let html = `<div class="bivariate-legend">`
    html += `<div class="legend-title" style="margin-bottom:8px;font-size:11px;">${config.title || 'Bivariate'}</div>`

    // Container: Y-axis label + grid + X-axis label
    html += `<div style="display:flex;align-items:center;gap:6px;">`

    // Y-axis label (attribute2, rotated)
    html += `<div style="writing-mode:vertical-rl;transform:rotate(180deg);font-size:9px;color:var(--text-muted,#aaa);white-space:nowrap;max-height:${size * 3 + gap * 2 + 4}px;overflow:hidden;text-overflow:ellipsis;">${attribute2} →</div>`

    // 3×3 grid (row 2 = top = high attr2, row 0 = bottom = low attr2)
    html += `<div style="display:grid;grid-template-columns:repeat(3,${size}px);grid-template-rows:repeat(3,${size}px);gap:${gap}px;">`
    for (let row = 2; row >= 0; row--) {
      for (let col = 0; col < 3; col++) {
        const color = palette[row][col]
        const a1Label = ['Low', 'Mid', 'High'][col]
        const a2Label = ['Low', 'Mid', 'High'][row]
        html += `<div style="width:${size}px;height:${size}px;background:${color};border-radius:2px;cursor:default;" title="${attribute1}: ${a1Label}, ${attribute2}: ${a2Label}"></div>`
      }
    }
    html += `</div>`
    html += `</div>`

    // X-axis label (attribute1)
    html += `<div style="text-align:center;font-size:9px;color:var(--text-muted,#aaa);margin-top:4px;margin-left:20px;">${attribute1} →</div>`

    // No data indicator
    html += `<div style="display:flex;align-items:center;gap:4px;margin-top:6px;font-size:9px;color:var(--text-muted,#888);">`
    html += `<div style="width:12px;height:12px;background:${noDataColor || '#d9d9d9'};border-radius:2px;"></div>`
    html += `<span>No data</span>`
    html += `</div>`

    html += `</div>`
    // SECURITY: sanitized 2026-04-18 — config.title, attribute1, attribute2 may derive from intent
    this.panel.innerHTML = DOMPurify.sanitize(html)
  }

  _colorStr(color) {
    if (Array.isArray(color)) {
      return `rgba(${color[0]},${color[1]},${color[2]},${(color[3] ?? 255) / 255})`
    }
    return color
  }

  hide() {
    this.panel.classList.remove('visible')
    this.panel.innerHTML = ''
    this._currentConfig = null
    this._collapsed = true
  }
}

export { Legend }
export default Legend
